/**
 * Modal showing BVP operation logs for a requirement.
 */

import { useEffect, useState } from 'react';
import { Modal, Table, Spin, Empty, Tag, Descriptions, Typography, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getRequirementOperationLogs } from '@/api/requirements';
import type { RequirementOperationLog } from '@/types';

const { Text } = Typography;

// Field label mapping for Chinese display
const FIELD_LABELS: Record<string, string> = {
  reqId: '需求ID',
  owner: '责任人',
  description: '需求描述',
  deliveryStage: '交付阶段',
  linkedPackage: '需求交付首版软件',
  status: '状态',
  subcategory: '子类别',
};

// Action type color mapping
const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  sync: 'cyan',
};

interface RequirementLogModalProps {
  open: boolean;
  projectId: number;
  requirementId: string;
  onClose: () => void;
}

const RequirementLogModal: React.FC<RequirementLogModalProps> = ({
  open,
  projectId,
  requirementId,
  onClose,
}) => {
  const [logs, setLogs] = useState<RequirementOperationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getRequirementOperationLogs(projectId, requirementId)
      .then(setLogs)
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [open, projectId, requirementId]);

  // Utility functions
  const hasChanges = (record: RequirementOperationLog): boolean => {
    return !!(record.beforeData && record.afterData);
  };

  const formatFieldValue = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return value ? '是' : '否';
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '(空)';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const computeChangedFields = (
    beforeData: Record<string, unknown>,
    afterData: Record<string, unknown>
  ): Record<string, { before: string | null; after: string | null }> => {
    const changes: Record<string, { before: string | null; after: string | null }> = {};

    const allFields = new Set([
      ...Object.keys(beforeData),
      ...Object.keys(afterData),
    ]);

    allFields.forEach((field) => {
      const before = beforeData[field];
      const after = afterData[field];

      if (JSON.stringify(before) !== JSON.stringify(after)) {
        changes[field] = {
          before: formatFieldValue(before),
          after: formatFieldValue(after),
        };
      }
    });

    return changes;
  };

  const renderExpandedRow = (record: RequirementOperationLog) => {
    // Fields to exclude from display
    const excludedFields = ['id', 'version'];

    // Special Case: Status update operation
    if (record.action === 'status_update' && record.afterData) {
      const statusChanges = (record.afterData as any).status_changes || [];

      return (
        <div style={{ padding: '12px 48px' }}>
          <Alert
            title="状态更新"
            description="批量更新需求状态"
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Descriptions column={1} size="small" bordered>
            {statusChanges.map((change: any, idx: number) => (
              <Descriptions.Item key={idx} label={change.reqId || `需求 #${change.id}`}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {change.old_status && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        变更前:{' '}
                      </Text>
                      <Text delete style={{ color: '#ff4d4f' }}>
                        {change.old_status}
                      </Text>
                    </div>
                  )}
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      变更后:{' '}
                    </Text>
                    <Text strong style={{ color: '#52c41a' }}>
                      {change.status}
                    </Text>
                  </div>
                </div>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      );
    }

    // Case 1: Normal update (both before and after data)
    if (record.beforeData && record.afterData) {
      const changedFields = computeChangedFields(record.beforeData, record.afterData);

      // Filter out excluded fields
      const filteredChangedFields = Object.entries(changedFields)
        .filter(([field]) => !excludedFields.includes(field));

      if (filteredChangedFields.length === 0) {
        return (
          <div style={{ padding: '12px 48px' }}>
            <Empty description="无字段变更" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        );
      }

      return (
        <div style={{ padding: '12px 48px' }}>
          <Descriptions
            column={1}
            size="small"
            bordered
            styles={{
              label: { width: 150, fontWeight: 500 },
              content: { wordBreak: 'break-word', whiteSpace: 'pre-wrap' }
            }}
          >
            {filteredChangedFields.map(([field, change]) => (
              <Descriptions.Item
                key={field}
                label={FIELD_LABELS[field] || field}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {change.before !== null && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        变更前:{' '}
                      </Text>
                      <Text delete style={{ color: '#ff4d4f' }}>
                        {change.before || '(空)'}
                      </Text>
                    </div>
                  )}
                  {change.after !== null && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        变更后:{' '}
                      </Text>
                      <Text strong style={{ color: '#52c41a' }}>
                        {change.after || '(空)'}
                      </Text>
                    </div>
                  )}
                </div>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      );
    }

    // Case 2: Create operation (only after data)
    if (!record.beforeData && record.afterData) {
      const filteredEntries = Object.entries(record.afterData)
        .filter(([key]) => !excludedFields.includes(key));

      return (
        <div style={{ padding: '12px 48px' }}>
          <Alert
            title="创建操作"
            description="该操作创建了新的需求记录"
            type="success"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Descriptions column={1} size="small" bordered>
            {filteredEntries.map(([key, value]) => (
              <Descriptions.Item key={key} label={FIELD_LABELS[key] || key}>
                <Text strong style={{ color: '#52c41a' }}>
                  {formatFieldValue(value) || '(空)'}
                </Text>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      );
    }

    // Case 3: Delete operation (only before data)
    if (record.beforeData && !record.afterData) {
      const filteredEntries = Object.entries(record.beforeData)
        .filter(([key]) => !excludedFields.includes(key));

      return (
        <div style={{ padding: '12px 48px' }}>
          <Alert
            title="删除操作"
            description="该操作删除了需求记录"
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Descriptions column={1} size="small" bordered>
            {filteredEntries.map(([key, value]) => (
              <Descriptions.Item key={key} label={FIELD_LABELS[key] || key}>
                <Text delete style={{ color: '#ff4d4f' }}>
                  {formatFieldValue(value) || '(空)'}
                </Text>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      );
    }

    // Case 4: No data
    return (
      <div style={{ padding: '12px 48px' }}>
        <Empty description="无详细变更信息" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  };

  const columns: ColumnsType<RequirementOperationLog> = [
    { title: '操作人', dataIndex: 'operator', key: 'operator', width: 100 },
    {
      title: '操作',
      dataIndex: 'actionDisplay',
      key: 'action',
      width: 120,
      render: (text: string, record) => (
        <Tag color={ACTION_COLORS[record.action] || 'default'}>
          {text}
        </Tag>
      ),
    },
    {
      title: '描述',
      key: 'description',
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.id);
        const toggleExpand = (e: React.MouseEvent) => {
          e.stopPropagation();
          setExpandedRowKeys((prev) =>
            isExpanded ? prev.filter((key) => key !== record.id) : [...prev, record.id]
          );
        };

        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <Text>{record.description}</Text>
            {hasChanges(record) && (
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  marginLeft: 8,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={toggleExpand}
              >
                ({isExpanded ? '点击收起' : '点击展开查看详情'})
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      render: (version: string | null | undefined) => version || '-',
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (val: string) => new Date(val).toLocaleString('zh-CN'),
      defaultSortOrder: 'descend',
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
  ];

  return (
    <Modal
      title={`操作日志 — ${requirementId}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : logs.length === 0 ? (
        <Empty description="暂无操作日志" />
      ) : (
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20 }}
          expandable={{
            expandedRowRender: renderExpandedRow,
            rowExpandable: (record) => !!(record.beforeData || record.afterData),
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as number[]),
          }}
        />
      )}
    </Modal>
  );
};

export default RequirementLogModal;
